import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import store from 'store';

import useAuthenticated from '../../../../../hooks/useAuthenticated';
import {
  clientStoreGet,
  MeQuery,
} from '../../../../../graphql/queries/account';
import {
  albumCreate as albumCreateMutation,
  albumUpdate as albumUpdateMutation,
} from '../../../../../graphql/mutations/albums';
import { albumFolderUpdate as albumFolderUpdateMutation } from '../../../../../graphql/mutations/album-folder';
import { ALBUM_TYPE } from '../../../../../constants/albums';
import { getEventTutorialSteps, processTutorialStep } from './helpers';
import { getErrorString } from '../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import useEventAddonPurchase from '../../../components/FreemiumEventPurchase/hooks/useEventAddonPurchase';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import useCloseModal from '../../../../../hooks/useCloseModal';
import useOpenModal from '../../../../../hooks/useOpenModal';
import { isAlbum } from '../../../../../util/album';
import { clientStoreSet as clientStoreSetMutation } from '../../../../../graphql/mutations/account';
import { FREEMIUM_EVENT_TUTORIAL_STEPS } from './constants';
import useIsNativeWebView from '../../../../../services/native-bridge/hooks/useIsWebView';

const TUTORIAL_KEY = '__WALDO_EVENT_TUTORIAL__';

/**
 * Hook to handle the Event tutorial wizard.
 *
 * @param {object} opts List of opts.
 * @param {object} opts.entity The entity to apply tutorial for.
 * @param {function} opts.onCheckoutSuccess Callback that happens on checkout success.
 * @param {function} opts.onStepChanged Callback that happens on every step change.
 * @param {Object} opts.configuration relevant pre-existing configuration..
 * @returns {{onSkip: onSkip, submitting: boolean, albumFolderUpdate: (options?: MutationFunctionOptions<any, OperationVariables>) => Promise<void | ExecutionResult<any>>, modalOpen: boolean, setModalOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void, clientStore: *, activeStepIndex: (number|number), steps: ({onSkip: function({handleChange: *}): void, alertLabel: string, skip, label: string, skippable: function(): boolean, title: string, modalActionBtnLabel: string, modalSkipBtnLabel: string, skipBtnLabel: string, component: function(*): *, layoutSize: string, actionBtnLabel: string, key: string, validate: function(*, *): Promise<{}>} | {skipBtnLabel: string, component: function(*): *, alertLabel: string, layoutSize: string, skip, title: string, actionBtnLabel: null|string, modalActionBtnLabel: string, modalSkipBtnLabel: null|string, key: string} | {skipBtnLabel: string, component: function(): *, alertLabel: string, layoutSize: string, skippable: function(): boolean, title: string, actionBtnLabel: string, modalActionBtnLabel: string, modalSkipBtnLabel: string, key: string} | {onSkip: function({handleChange: *}): void, alertLabel: string, skip, label: string, skippable: function(): boolean, modalActionBtnLabel: string, modalSkipBtnLabel: string, skipBtnLabel: string, component: function(*): *, layoutSize: string, actionBtnLabel: string, key: string, validate: function(*): {}} | {skipBtnLabel: string, component: function(*): *, alertLabel: string, layoutSize: string, skip: boolean, label: string, title: string, actionBtnLabel: string, modalSkipBtnLabel: string, key: string} | {skipBtnLabel: string, component: function(): *, alertLabel: string, layoutSize: string, skip: boolean, label: string, title: string, actionBtnLabel: string, modalActionBtnLabel: string, key: string, validate: function()})[], stepProps: {purchasedAddonItems, eventAddonItems: *[], setActiveStepIndex: (value: (((prevState: (number|number)) => (number|number)) | number | number)) => void, activeStepIndex: (number|number), entity}, onStep: ((function(*): Promise<void>)|*), eventAddonItems: *[], clientStoreTutorialComplete: boolean, albumCreate: (options?: MutationFunctionOptions<any, OperationVariables>) => Promise<void | ExecutionResult<any>>, setStepProps: (value: (((prevState: {purchasedAddonItems, eventAddonItems: *[], setActiveStepIndex: (value: (((prevState: (number|number)) => (number|number)) | number | number)) => void, activeStepIndex: (number|number), entity}) => {purchasedAddonItems, eventAddonItems: *[], setActiveStepIndex: (value: (((prevState: (number|number)) => (number|number)) | number | number)) => void, activeStepIndex: (number|number), entity}) | {purchasedAddonItems, eventAddonItems: *[], setActiveStepIndex: (value: (((prevState: (number|number)) => (number|number)) | number | number)) => void, activeStepIndex: (number|number), entity})) => void, setActiveStepIndex: (value: (((prevState: (number|number)) => (number|number)) | number | number)) => void, user: ({id}|*), isComplete: unknown}}
 */
const useFreemiumEventTutorialWizard = ({
  entity,
  onCheckoutSuccess,
  onStepChanged,
  configuration = {},
}) => {
  const isNativeWebView = useIsNativeWebView(configuration?.stepKeys);
  const entityTutorialKey = `${TUTORIAL_KEY}__${entity?.id}`;
  const authenticated = useAuthenticated();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const { data: { clientStoreGet: clientStore } = {} } = useQuery(
    clientStoreGet,
    {
      variables: {
        accountId: user?.id,
        keys: [entityTutorialKey],
      },
      skip: !entity?.id || !user?.id || !entityTutorialKey,
    },
  );
  const [albumCreate] = useMutation(albumCreateMutation);
  const [albumUpdate] = useMutation(albumUpdateMutation);
  const [albumFolderUpdate] = useMutation(albumFolderUpdateMutation);
  const [clientStoreSet] = useMutation(clientStoreSetMutation);

  const onClientStoreSet = async input => {
    clientStoreSet({
      variables: {
        input,
      },
    });
  };

  const [modalOpen, setModalOpen] = useState(false);
  const tutorialStore = store.get(entityTutorialKey) || {};
  const config = {
    ...tutorialStore,
    ...configuration,
  };

  const [activeStepIndex, setActiveStepIndex] = useState(
    config?.stepKeys?.length > 0 && config?.activeStepKey
      ? (config?.stepKeys ?? []).indexOf(config?.activeStepKey)
      : 0,
  );
  const openAlert = useOpenAlert();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const [isComplete, setIsComplete] = useState(config?.isComplete || false);
  const isAlbumEntity = isAlbum(entity);
  const clientStoreTutorialComplete =
    clientStore?.data?.[entityTutorialKey] === 'true';

  const {
    submitting,
    onCheckout,
    eventAddonItems,
    loadingEventAddonItems,
    purchasedAddonItems,
  } = useEventAddonPurchase({
    defaultEventAddonItemTypes: [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
    ],
    albumFolderId: isAlbumEntity ? undefined : entity.id,
    albumId: isAlbumEntity ? entity.id : undefined,
    onCheckoutSuccess: async ({ steps }) => {
      onCheckoutSuccess?.();

      if (
        steps[activeStepIndex].key ===
        FREEMIUM_EVENT_TUTORIAL_STEPS.QR_CODE_DOWNLOAD
      ) {
        // Let the user download their QR :)
        return;
      }

      const updatedStepIndex = Math.min(activeStepIndex + 1, steps.length - 1);
      setActiveStepIndex(updatedStepIndex);
    },
    onCheckoutError: ({ error }) => {
      openAlert({ type: 'error', message: getErrorString(error) });
    },
  });
  const [stepProps, setStepProps] = useState({
    entity,
    activeStepIndex,
    setActiveStepIndex,
    eventAddonItems,
    purchasedAddonItems,
  });

  const stepsObject = getEventTutorialSteps({ stepProps, setStepProps });
  const steps = config?.stepKeys
    ? Object.values(stepsObject).filter(({ key }) =>
        config.stepKeys.includes(key),
      )
    : Object.values(stepsObject).filter(({ skip }) => !skip);

  const activeStepKey = steps[activeStepIndex]?.key;

  useEffect(() => {
    if (entity?.album_type === ALBUM_TYPE.EVENT) {
      const data = {
        ...config,
        stepKeys: config?.stepKeys || steps.map(step => step.key),
        activeStepKey,
        isComplete: isComplete || clientStoreTutorialComplete,
      };

      if (isNativeWebView) {
        onStepChanged?.(data);
      } else {
        store.set(entityTutorialKey, data);
      }
    }
  }, [entity?.id, activeStepKey, isComplete, clientStoreTutorialComplete]);

  useEffect(() => {
    if (!loadingEventAddonItems && !stepProps?.eventAddonItems?.length) {
      setStepProps({
        ...stepProps,
        eventAddonItems,
        purchasedAddonItems,
      });
    }
  }, [loadingEventAddonItems]);

  useEffect(() => {
    setStepProps({
      ...stepProps,
      // Ensure we update to latest copy of
      // purchasedAddonItems
      purchasedAddonItems,
    });
  }, [purchasedAddonItems]);

  useEffect(() => {
    if (isComplete && !clientStoreTutorialComplete) {
      onClientStoreSet({
        account_id: user.id,
        key: entityTutorialKey,
        data: isComplete,
      });
    }
  }, [isComplete]);

  const onStep = async values => {
    const activeStep = steps[activeStepIndex];
    const isLastStep = activeStepIndex === steps.length - 1;

    try {
      const { stepComplete } = await processTutorialStep({
        user,
        steps,
        entity,
        values,
        openModal,
        closeModal,
        activeStep,
        albumCreate,
        albumUpdate,
        albumFolderUpdate,
        onEventAddonPurchaseCheckout: onCheckout,
        purchasedAddonItems,
        eventAddonItems,
        onCheckoutSuccess,
        isNativeWebView,
      });

      // Avoids over stepping.
      if (stepComplete) {
        const updatedStepIndex = Math.min(
          activeStepIndex + 1,
          steps.length - 1,
        );

        setActiveStepIndex(updatedStepIndex);

        if (isLastStep) {
          setIsComplete(true);
        }
      }
    } catch (err) {
      openAlert({
        message: 'There was an error accepting this request.',
        info: getErrorString(err),
      });
    }
  };

  const onSkip = () => {
    const isLastStep = activeStepIndex === steps.length - 1;

    // Avoids over stepping.
    setActiveStepIndex(Math.min(activeStepIndex + 1, steps.length - 1));

    if (isLastStep) {
      setIsComplete(true);
    }
  };

  return {
    user,
    albumCreate,
    albumFolderUpdate,
    modalOpen,
    setModalOpen,
    activeStepIndex,
    setActiveStepIndex,
    steps,
    stepProps,
    setStepProps,
    onStep,
    onSkip,
    isComplete,
    eventAddonItems,
    submitting,
    clientStore,
    clientStoreTutorialComplete,
  };
};

export default useFreemiumEventTutorialWizard;
